import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Accordion, Button, Col, Form, Modal, Row } from "react-bootstrap";
import { Input, Label } from "reactstrap";
import AsyncCreatableSelect from "react-select/async-creatable";
import PhoneInput from "react-phone-input-2";
import { toast } from "react-toastify";
import CurrencyAmountInput from "./CurrencyAmountInput";
import { masterPharmaDoctorsAPI } from "../../../PharmaDoctors/PharmaDoctorService";
import {
	EMPTY_DOCTOR_FORM,
	hasDoctorFormInput,
	isPlaceholderDoctorName,
	mapMasterDoctorToForm,
	buildDoctorSelectOption,
	sanitizeDoctorName,
} from "../utils/doctorFormUtils";
import "./PaymentModal.scss";

const ALLOWED_PRESCRIPTION_MIME_TYPES = [
	"application/pdf",
	"application/msword",
	"application/vnd.openxmlformats-officedocument.wordprocessingml.document",
	"text/plain",
	"application/rtf",
	"application/vnd.oasis.opendocument.text",
	"image/jpeg",
	"image/png",
];

const mapDoctorToForm = mapMasterDoctorToForm;

const buildDoctorSearchLabel = (doctor) => buildDoctorSelectOption(doctor).label;

const buildNewDoctorOption = (name) => {
	const trimmed = sanitizeDoctorName(name);
	if (!trimmed) return null;
	return {
		value: `new:${trimmed}`,
		label: trimmed,
		isNew: true,
	};
};

const truncateFileName = (name, max = 22) => {
	if (!name || name.length <= max) return name;
	const dotIndex = name.lastIndexOf(".");
	const ext = dotIndex > 0 ? name.slice(dotIndex) : "";
	const base = ext ? name.slice(0, name.length - ext.length) : name;
	const keep = max - ext.length - 3;
	return keep > 0 ? `${base.slice(0, keep)}...${ext}` : `${name.slice(0, max - 3)}...`;
};

export const PaymentModal = ({
	t,
	show,
	onHide,
	paymentData,
	onPaymentDataChange,
	paidType,
	onPaidTypeChange,
	customerType,
	errors,
	handleSave,
	getCurrencySymbol,
	disableScrollProps,
	totals,
	handleSettlePayment,
	convertCurrency,
	defaultCurrency,
	canUploadPrescription,
	prescriptionFile,
	onPrescriptionFileChange,
	prescriptionDate,
	onPrescriptionDateChange,
	doctorForm,
	onDoctorFormChange,
	onClearDoctorSelection,
	doctorId,
	onDoctorIdChange,
	selectedDoctorOption,
	onSelectedDoctorOptionChange,
	onAddDoctorToMaster,
	addingDoctor,
	doctorFormVersion,
}) => {
	const fileInputRef = useRef(null);
	const today = new Date().toISOString().split("T")[0];
	const isDark = typeof document !== "undefined" && document.body.classList.contains("dark-only");
	const [doctorAccordionOpen, setDoctorAccordionOpen] = useState("");

	useEffect(() => {
		if (!show) {
			setDoctorAccordionOpen("");
			return;
		}
		if (errors.doctorName || errors.doctorEmail) {
			setDoctorAccordionOpen("doctor-details");
		}
	}, [show, errors.doctorName, errors.doctorEmail]);

	const selectStyles = useMemo(
		() => ({
			menuPortal: (base) => ({ ...base, zIndex: 9999 }),
			menu: (base) => ({
				...base,
				zIndex: 9999,
				backgroundColor: isDark ? "#1d1e26" : base.backgroundColor,
				color: isDark ? "#e2e2e2" : base.color,
			}),
			option: (base, state) => ({
				...base,
				backgroundColor: state.isFocused
					? isDark
						? "#373737"
						: "#e6f7ff"
					: isDark
						? "#1d1e26"
						: base.backgroundColor,
				color: isDark ? "#e2e2e2" : base.color,
			}),
			singleValue: (base) => ({
				...base,
				color: isDark ? "#e2e2e2" : base.color,
			}),
			input: (base) => ({
				...base,
				color: isDark ? "#e2e2e2" : base.color,
			}),
		}),
		[isDark]
	);

	const loadDoctorOptions = useCallback(
		async (inputValue) => {
			const search = inputValue?.trim();
			if (!search || search.length < 2) return [];

			try {
				const response = await masterPharmaDoctorsAPI({ search });
				const doctors = response?.data?.data || [];
				return doctors.map((doctor) => ({
					value: doctor._id,
					label: buildDoctorSearchLabel(doctor),
					data: doctor,
				}));
			} catch (error) {
				console.error("Error searching doctors:", error);
				return [];
			}
		},
		[]
	);

	const handleDoctorSelect = (option) => {
		if (!option) {
			onClearDoctorSelection();
			setDoctorAccordionOpen("");
			return;
		}

		// Typed name with no API match — draft doctor; created on pay/generate bill
		if (option.__isNew__ || option.isNew) {
			const name = sanitizeDoctorName(option.label || option.value);
			if (!name) {
				onClearDoctorSelection();
				return;
			}
			onDoctorIdChange(null);
			onSelectedDoctorOptionChange(buildNewDoctorOption(name));
			onDoctorFormChange({
				...EMPTY_DOCTOR_FORM,
				name,
			});
			setDoctorAccordionOpen("doctor-details");
			return;
		}

		onSelectedDoctorOptionChange(option);
		onDoctorIdChange(option.value);
		onDoctorFormChange(mapDoctorToForm(option.data));
		setDoctorAccordionOpen("doctor-details");
	};

	const handleDoctorFieldChange = (field, value) => {
		if (doctorId && field !== "note") {
			onDoctorIdChange(null);
			onSelectedDoctorOptionChange(null);
		}
		onDoctorFormChange({ [field]: value });
		if (field === "name" && !doctorId) {
			const option = buildNewDoctorOption(value);
			onSelectedDoctorOptionChange(option);
		}
	};

	const handlePhoneChange = (value, country) => {
		if (doctorId) {
			onDoctorIdChange(null);
			onSelectedDoctorOptionChange(null);
		}
		const dialCode = country?.dialCode ? `+${country.dialCode}` : doctorForm.phoneCode;
		const phoneNumber = value.slice(country?.dialCode?.length || 0);
		onDoctorFormChange({
			phoneCode: dialCode,
			phoneNumber,
		});
	};

	const getPhoneValue = () => {
		const code = (doctorForm.phoneCode || "").replace(/\D/g, "");
		const number = (doctorForm.phoneNumber || "").replace(/\D/g, "");
		return code && number ? `${code}${number}` : number;
	};

	const handlePrescriptionFileChange = (event) => {
		const file = event.target.files?.[0];
		if (!file) return;

		if (file.size > 5 * 1024 * 1024) {
			toast.warning(t("Prescription file size should be less than 5MB"));
			event.target.value = "";
			return;
		}

		if (!ALLOWED_PRESCRIPTION_MIME_TYPES.includes(file.type)) {
			toast.warning(t("Unsupported file format"));
			event.target.value = "";
			return;
		}

		onPrescriptionFileChange(file);
		event.target.value = "";
	};

	const handleRemovePrescription = () => {
		onPrescriptionFileChange(null);
		onPrescriptionDateChange("");
		if (fileInputRef.current) {
			fileInputRef.current.value = "";
		}
	};

	const handlePreviewPrescription = () => {
		if (prescriptionFile) {
			window.open(URL.createObjectURL(prescriptionFile), "_blank");
		}
	};

	const showAddDoctorButton = !doctorId && hasDoctorFormInput(doctorForm);
	const isDraftDoctor = !doctorId && Boolean(sanitizeDoctorName(doctorForm?.name));

	const doctorAccordionLabel =
		doctorForm.name?.trim() && !isPlaceholderDoctorName(doctorForm.name)
			? doctorForm.name.trim()
			: t("Doctor Details");

	const showDoctorDetailsAccordion = doctorId || hasDoctorFormInput(doctorForm);

	const optionalStatusItems = useMemo(() => {
		const items = [];
		if (doctorId) {
			items.push({ key: "doctor-linked", label: t("Doctor linked to sale"), variant: "success" });
		} else if (isDraftDoctor) {
			items.push({
				key: "doctor-draft",
				label: t("New doctor — will be created when you pay"),
				variant: "warning",
			});
		}
		return items;
	}, [doctorId, isDraftDoctor, t]);

	const convertedTotal = convertCurrency(
		totals.netTotal,
		defaultCurrency,
		paymentData.currency
	);
	const remainingAmount = Math.max(0, convertedTotal - Number(paymentData.amount || 0));

	return (
		<Modal
			show={show}
			onHide={onHide}
			centered
			size="lg"
			dialogClassName="payment-modal__dialog"
			className="payment-modal"
			enforceFocus={false}
			restoreFocus={false}
		>
			<Modal.Header closeButton className="payment-modal__header">
				<Modal.Title className="payment-modal__title">
					<i className="fa fa-money me-2" />
					{t("Payment Process")}
				</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				<Row className="payment-modal__columns g-2">
					<Col xs={12} lg={6} className="payment-modal__col">
						<div className="payment-modal__section">
							{optionalStatusItems.length > 0 && (
								<div className="payment-modal__status-chips mb-2">
									{optionalStatusItems.map((item) => (
										<span
											key={item.key}
											className={`payment-modal__status-chip payment-modal__status-chip--${item.variant}`}
										>
											{item.label}
										</span>
									))}
								</div>
							)}

							{canUploadPrescription && (
								<div className="payment-modal__prescription-compact mb-2">
									<input
										ref={fileInputRef}
										type="file"
										accept=".pdf,.doc,.docx,.txt,.rtf,.odt,.jpg,.jpeg,.png"
										onChange={handlePrescriptionFileChange}
										className="d-none"
									/>

									<div className="payment-modal__prescription-heading">
										<i className="fa fa-file-text-o" aria-hidden="true" />
										<span>{t("Prescription")}</span>
										<span className="payment-modal__optional-badge">{t("Optional")}</span>
									</div>

									<div className="payment-modal__prescription-actions-row">
										{prescriptionFile ? (
											<Button
												variant="outline-primary"
												size="sm"
												type="button"
												className="payment-modal__action-btn"
												onClick={handlePreviewPrescription}
												title={prescriptionFile.name}
											>
												<i className="fa fa-eye" aria-hidden="true" />
												{t("View")}
											</Button>
										) : (
											<Button
												variant="outline-primary"
												size="sm"
												type="button"
												className="payment-modal__action-btn"
												onClick={() => fileInputRef.current?.click()}
											>
												<i className="fa fa-upload" aria-hidden="true" />
												{t("Upload")}
											</Button>
										)}


										{prescriptionFile && (
											<>
												{/* <Button
													variant="outline-secondary"
													size="sm"
													type="button"
													className="payment-modal__action-btn"
													onClick={() => fileInputRef.current?.click()}
													title={
														prescriptionFile
															? t("Replace file")
															: t("Upload a file first")
													}
												>
													<i className="fa fa-refresh" aria-hidden="true" />
													{t("Re Upload")}
												</Button> */}
												<Button
													variant="outline-danger"
													size="sm"
													type="button"
													className="payment-modal__icon-btn payment-modal__icon-btn--danger"
													onClick={handleRemovePrescription}
													title={t("Remove file")}
												>
													<i className="fa fa-trash-o" aria-hidden="true" />
												</Button>
											</>
										)}

										<div className="payment-modal__prescription-date">
											<label className="payment-modal__date-label" htmlFor="prescription-date">
												{t("Date")}
											</label>
											<Input
												id="prescription-date"
												type="date"
												className="formControlColor payment-modal__date-input"
												bsSize="sm"
												value={prescriptionDate}
												max={today}
												onChange={(e) => onPrescriptionDateChange(e.target.value)}
											/>
										</div>
									</div>

									{prescriptionFile && (
										<div className="payment-modal__file-hint" title={prescriptionFile.name}>
											<i className="fa fa-paperclip me-1" aria-hidden="true" />
											{truncateFileName(prescriptionFile.name, 40)}
										</div>
									)}
								</div>
							)}

							<div className="payment-modal__subsection">
								<div className="payment-modal__section-title">
									<i className="fa fa-user-md" />
									{t("Prescribed Doctor")}
									<span className="payment-modal__optional-badge">{t("Optional")}</span>
								</div>
								<Form.Group className="mb-2">
									<Form.Label className="small mb-1">{t("Search Doctor")}</Form.Label>
									<AsyncCreatableSelect
										inputId="payment-doctor-search"
										classNamePrefix="react-select"
										cacheOptions
										isClearable
										allowCreateWhileLoading={false}
										loadOptions={loadDoctorOptions}
										value={selectedDoctorOption}
										onChange={handleDoctorSelect}
										placeholder={t("Search by name, email or phone...")}
										formatCreateLabel={(inputValue) =>
											`${t("Use")} "${inputValue.trim()}"`
										}
										isValidNewOption={(inputValue, _value, options) => {
											const search = String(inputValue || "").trim();
											if (search.length < 2) return false;
											const existing = (options || []).filter((opt) => !opt.__isNew__);
											return existing.length === 0;
										}}
										noOptionsMessage={({ inputValue }) =>
											!inputValue || inputValue.length < 2
												? t("Type at least 2 characters")
												: t("No doctors found — use the name above to continue")
										}
										menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
										menuPosition="fixed"
										menuShouldScrollIntoView
										styles={selectStyles}
										theme={(theme) => ({
											...theme,
											colors: {
												...theme.colors,
												primary25: isDark ? "#373737" : "#e6f7ff",
											},
										})}
									/>
									{isDraftDoctor && (
										<p className="payment-modal__helper-text mb-0 mt-1">
											{t("This doctor will be created automatically when you pay.")}
										</p>
									)}
								</Form.Group>

								{showDoctorDetailsAccordion ? (
								<Accordion
									activeKey={doctorAccordionOpen}
									onSelect={(key) => setDoctorAccordionOpen(key || "")}
									className="payment-modal__doctor-accordion"
								>
									<Accordion.Item eventKey="doctor-details">
										<Accordion.Header>
											<span className="text-truncate">{doctorAccordionLabel}</span>
											{!doctorForm.name?.trim() && (
												<span className="payment-modal__doctor-summary ms-2">
													({t("expand to enter details")})
												</span>
											)}
										</Accordion.Header>
										<Accordion.Body>
											<Row className="g-2">
												<Col xs={12} sm={6}>
													<Form.Label className="small mb-1">{t("Doctor Name")}</Form.Label>
													<Input
														type="text"
														bsSize="sm"
														value={doctorForm.name}
														placeholder={t("e.g. Dr. Rahul")}
														onChange={(e) => handleDoctorFieldChange("name", e.target.value)}
														invalid={Boolean(errors.doctorName)}
													/>
													{errors.doctorName && (
														<div className="text-danger small mt-1">{errors.doctorName}</div>
													)}
												</Col>
												<Col xs={12} sm={6}>
													<Form.Label className="small mb-1">
														{t("Registration / Licence No.")}
													</Form.Label>
													<Input
														type="text"
														bsSize="sm"
														value={doctorForm.registrationNumber}
														placeholder={t("e.g. MCI-12345")}
														onChange={(e) =>
															handleDoctorFieldChange("registrationNumber", e.target.value)
														}
													/>
												</Col>
												<Col xs={12} sm={6}>
													<Form.Label className="small mb-1">{t("Email")}</Form.Label>
													<Input
														type="email"
														bsSize="sm"
														value={doctorForm.email}
														placeholder={t("email@example.com")}
														onChange={(e) => handleDoctorFieldChange("email", e.target.value)}
														invalid={Boolean(errors.doctorEmail)}
													/>
													{errors.doctorEmail && (
														<div className="text-danger small mt-1">{errors.doctorEmail}</div>
													)}
												</Col>
												<Col xs={12} sm={6}>
													<Form.Label className="small mb-1">{t("Phone Number")}</Form.Label>
													<PhoneInput
														key={`doctor-phone-${doctorFormVersion}`}
														country="in"
														value={getPhoneValue()}
														onChange={handlePhoneChange}
														inputClass="form-control form-control-sm"
														containerClass="w-100"
														enableSearch
													/>
												</Col>
												<Col xs={12} sm={6}>
													<Form.Label className="small mb-1">{t("Qualification")}</Form.Label>
													<Input
														type="text"
														bsSize="sm"
														value={doctorForm.qualification}
														placeholder={t("e.g. MBBS, MD")}
														onChange={(e) =>
															handleDoctorFieldChange("qualification", e.target.value)
														}
													/>
												</Col>
												<Col xs={12} sm={6}>
													<Form.Label className="small mb-1">{t("Speciality")}</Form.Label>
													<Input
														type="text"
														bsSize="sm"
														value={doctorForm.speciality}
														placeholder={t("e.g. Cardiology")}
														onChange={(e) =>
															handleDoctorFieldChange("speciality", e.target.value)
														}
													/>
												</Col>
												<Col xs={12}>
													<Form.Label className="small mb-1">
														{t("Clinic / Hospital Name")}
													</Form.Label>
													<Input
														type="text"
														bsSize="sm"
														value={doctorForm.clinicName}
														placeholder={t("Clinic name")}
														onChange={(e) =>
															handleDoctorFieldChange("clinicName", e.target.value)
														}
													/>
												</Col>
												<Col xs={12}>
													<Form.Label className="small mb-1">{t("Clinic Address")}</Form.Label>
													<Input
														type="text"
														bsSize="sm"
														value={doctorForm.clinicAddress}
														placeholder={t("Clinic address")}
														onChange={(e) =>
															handleDoctorFieldChange("clinicAddress", e.target.value)
														}
													/>
												</Col>
												<Col xs={12}>
													<Form.Label className="small mb-1">{t("Note")}</Form.Label>
													<Input
														type="textarea"
														bsSize="sm"
														rows={2}
														value={doctorForm.note}
														placeholder={t("Additional notes")}
														onChange={(e) => handleDoctorFieldChange("note", e.target.value)}
													/>
												</Col>
											</Row>

											{showAddDoctorButton && (
												<div className="mt-2">
													<p className="payment-modal__helper-text mb-2">
														{t(
															"Optional: save now, or the doctor will be created automatically when you pay."
														)}
													</p>
													<div className="d-flex justify-content-end">
														<Button
															variant="outline-primary"
															size="sm"
															type="button"
															className="payment-modal__add-doctor-btn"
															disabled={addingDoctor}
															onClick={() => {
																setDoctorAccordionOpen("doctor-details");
																onAddDoctorToMaster();
															}}
														>
															{addingDoctor ? (
																<>
																	<span
																		className="spinner-border spinner-border-sm me-2"
																		role="status"
																	/>
																	{t("Saving...")}
																</>
															) : (
																<>
																	<i className="fa fa-plus me-1" />
																	{t("Add to Doctors List")}
																</>
															)}
														</Button>
													</div>
												</div>
											)}
										</Accordion.Body>
									</Accordion.Item>
								</Accordion>
								) : null}
							</div>
						</div>
					</Col>

					<Col xs={12} lg={6} className="payment-modal__col">
						<div className="payment-modal__section">
							<div className="payment-modal__section-title">
								<i className="fa fa-credit-card" />
								{t("Payment")}
							</div>
							<div className="payment-modal__section-body">
								<Form>
									<Form.Group className="mb-2">
										<Form.Label className="small mb-1">{t("Payment Type")}</Form.Label>
										<Form.Select
											size="sm"
											value={paymentData.type}
											onChange={(event) => onPaymentDataChange({ type: event.target.value })}
											isInvalid={!!errors.type}
										>
											<option value="cash">{t("Cash")}</option>
											<option value="card">{t("Card")}</option>
											<option value="upi">{t("UPI")}</option>
										</Form.Select>
										<Form.Control.Feedback type="invalid">{errors.type}</Form.Control.Feedback>
									</Form.Group>

									{customerType !== "walking" && (
										<Form.Group className="mb-2">
											<Form.Label className="small mb-1">{t("Payment")}</Form.Label>
											<Row className="g-2">
												<Col xs={6}>
													<div className="radio radio-primary">
														<Input
															id="paid-type-full"
															type="radio"
															name="paidType"
															value="full"
															checked={paidType === "full"}
															onChange={() => onPaidTypeChange("full")}
														/>
														<Label for="paid-type-full" className="ps-3 small">
															{t("Full Paid")}
														</Label>
													</div>
												</Col>
												<Col xs={6}>
													<div className="radio radio-primary">
														<Input
															id="paid-type-partial"
															type="radio"
															name="paidType"
															value="partial"
															checked={paidType === "partial"}
															onChange={() => onPaidTypeChange("partial")}
														/>
														<Label for="paid-type-partial" className="ps-3 small">
															{t("Partial Paid")}
														</Label>
													</div>
												</Col>
											</Row>
										</Form.Group>
									)}

									{totals.netTotal ? (
										<div className="small mb-2">
											<strong>{t("Total Amount")}:</strong>{" "}
											<span className="text-muted">
												{getCurrencySymbol(paymentData.currency)}
												{convertedTotal.toFixed(2)}
											</span>
										</div>
									) : null}

									<CurrencyAmountInput
										label={t("Paid")}
										amount={paymentData.amount}
										onAmountChange={(value) => onPaymentDataChange({ amount: value })}
										onCurrencyChange={(cur) => onPaymentDataChange({ currency: cur })}
										disabled={paidType === "full"}
										errors={errors}
										disableScrollProps={disableScrollProps}
										defaultCurrency={defaultCurrency}
										originalAmount={totals.netTotal}
									/>

									{customerType === "inpatient" && paidType === "partial" && (
										<div className="checkbox checkbox-solid-light mt-2 d-flex align-items-center">
											<Input
												id="isSettle-checkbox"
												type="checkbox"
												checked={paymentData.isSettle}
												className="me-2"
												onChange={handleSettlePayment}
											/>
											<label htmlFor="isSettle-checkbox" className="small mb-0">
												<strong>{t("Payment Settlement")}</strong>
											</label>
										</div>
									)}
								</Form>

								<div className="payment-modal__payment-summary">
									<div className="payment-modal__payment-info bg-primary-subtle">
										<div className="mb-1">
											<strong>{t("Payment")}:</strong>{" "}
											{paidType === "full" ? t("Full Paid") : t("Partial Paid")}
											{paidType === "partial" && (
												<span className="ms-2 text-danger">
													{t("Remaining")}: {getCurrencySymbol(paymentData.currency)}
													{remainingAmount.toFixed(2)}
												</span>
											)}
										</div>
										<div className="mb-0">
											<strong>{t("Payment Amount")}:</strong>{" "}
											{getCurrencySymbol(paymentData.currency)}
											{Number(paymentData.amount).toFixed(2) || 0}
										</div>
									</div>
								</div>
							</div>
						</div>
					</Col>
				</Row>
			</Modal.Body>
			<Modal.Footer>
				<Button variant="primary" onClick={handleSave}>
					{t("Generate Bill")}
				</Button>
				<Button variant="secondary" onClick={onHide}>
					{t("Cancel")}
				</Button>
			</Modal.Footer>
		</Modal>
	);
};

export default PaymentModal;
